import { Box, Divider, Theme, Typography } from '@material-ui/core';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import { createStyles, makeStyles } from '@material-ui/styles';
import classNames from 'classnames';
import React, { useEffect } from 'react';
import { Link, RouteComponentProps, withRouter } from 'react-router-dom';
import { SideNavItemInterface } from '../../../content-blocks/common/routes-map-model';
import { useCampfireTheme } from '../../../theme/useCampfireTheme';
import { useChatUnread } from '../../chat/ChatUnreadContext';
import { usePageNotFoundContext } from '../../page-not-found/PageNotFoundContext';

interface Props {
  closeDrawer: () => void;
  isDrawerOpen: boolean;
  activeSideNavRoute: string;
  setActiveSideNavRoute: (route: string) => void;
  subListItem: SideNavItemInterface;
  urlPath: string;
}

type DrawerNavListProps = Props & RouteComponentProps;

const useDesktopStyles = makeStyles((theme: Theme) =>
  createStyles({
    listItemStyle: {
      '&:hover': {
        background: 'transparent',
      },
      padding: '0 25px',
    },
    listItemTextContainerStyle: {
      borderRadius: theme.appShell.drawer.sideNav.itemBorderRadius,
      margin: 0,
      '&:hover': {
        background: 'rgba(255, 255, 255, 0.15)',
      },
      '&:hover p': {
        color: '#fcfcfc',
      },
    },
    listItemTextContainerActiveStyle: {
      background: 'rgba(255, 255, 255, 0.15)',
      color: '#fcfcfc',
    },
    listItemTextStyle: {
      fontSize: '0.9rem',
      color: 'rgba(255,255,255,0.5)',
    },
    listItemTextActiveStyle: {
      color: '#fcfcfc',
    },
  })
);

const useMobileStyles = makeStyles((theme: Theme) =>
  createStyles({
    listItemStyle: {
      padding: '0 16px',
    },
    listItemTextStyle: {},
    listItemTextActiveStyle: {
      color: 'white',
      margin: 0,
    },
    listItemTextContainerStyle: {
      borderRadius: theme.appShell.drawer.sideNav.itemBorderRadius,
    },
    listItemTextContainerActiveStyle: {
      background: '#242424',
    },
  })
);

type DrawerListItemProps = {
  isActive: boolean;
  closeDrawer: () => void;
  isDrawerOpen: boolean;
  label: string;
  customLabel?: string;
  id: string;
  isHidden?: boolean;
  onClick?: () => void;
  badgeCount?: number;
};

const NavBadge = ({ count }: { count: number }) => (
  <Box
    component='span'
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 18,
      height: 18,
      padding: '0 6px',
      marginLeft: 8,
      borderRadius: 9,
      background: '#f44336',
      color: '#fff',
      fontSize: '0.7rem',
      fontWeight: 600,
      lineHeight: 1,
    }}
  >
    {count > 99 ? '99+' : count}
  </Box>
);

const DrawerListItem = (props: DrawerListItemProps) => {
  const { isActive, isDrawerOpen, label, customLabel, isHidden, id, closeDrawer, onClick, badgeCount } = props;
  const { isMobile } = useCampfireTheme();
  const classes = isMobile ? useMobileStyles() : useDesktopStyles();

  return !isHidden ? (
    <ListItem
      component='nav'
      button
      onClick={() => {
        if (onClick) {
          onClick();
        }
        setTimeout(() => {
          if (isMobile) {
            closeDrawer();
          }
        }, 150);
      }}
      key={id}
      className={classNames(classes.listItemStyle)}
      disableRipple
      disableTouchRipple
    >
      <Divider orientation='vertical' flexItem style={{ background: '#444444', margin: '0 11px' }} />
      <ListItemText
        className={classNames(classes.listItemTextContainerStyle, {
          [classes.listItemTextContainerActiveStyle]: isActive,
        })}
        style={{ position: 'relative', padding: '8px 12px' }}
        primary={
          customLabel ? (
            <>
              <Typography
                className={classNames(classes.listItemTextStyle, {
                  [classes.listItemTextActiveStyle]: isActive,
                })}
                style={{
                  whiteSpace: isDrawerOpen ? 'normal' : 'nowrap',
                  fontSize: '0.6em',
                  marginBottom: '0px',
                  lineHeight: '1',
                }}
                variant='body1'
              >
                {label}
              </Typography>
              <Typography
                className={classNames(classes.listItemTextStyle, {
                  [classes.listItemTextActiveStyle]: isActive,
                })}
                style={{ whiteSpace: isDrawerOpen ? 'normal' : 'nowrap' }}
                variant='body1'
              >
                {customLabel}
              </Typography>
            </>
          ) : (
            <Typography
              className={classNames(classes.listItemTextStyle, {
                [classes.listItemTextActiveStyle]: isActive,
              })}
              style={{ whiteSpace: isDrawerOpen ? 'normal' : 'nowrap', display: 'flex', alignItems: 'center' }}
              variant='body1'
            >
              {label}
              {badgeCount !== undefined && badgeCount > 0 && <NavBadge count={badgeCount} />}
            </Typography>
          )
        }
      />
    </ListItem>
  ) : (
    <></>
  );
};

const DrawerNavListItem = withRouter(
  ({
    closeDrawer,
    isDrawerOpen,
    activeSideNavRoute,
    setActiveSideNavRoute,
    subListItem,
    urlPath,
    location,
  }: DrawerNavListProps) => {
    useEffect(() => {
      if (urlPath && location.pathname.startsWith(urlPath)) {
        setActiveSideNavRoute(urlPath);
      }
    }, [urlPath, location.pathname]);

    const isActive = activeSideNavRoute === urlPath;

    const { setPageNotFound } = usePageNotFoundContext();
    const { total: chatUnread } = useChatUnread();
    const isChatRoute = subListItem.route === 'chat';
    return (
      <Link
        to={urlPath}
        style={{ textDecoration: 'none', textDecorationColor: 'none', color: 'inherit' }}
        onClick={() => setPageNotFound(false)}
      >
        <DrawerListItem
          isActive={isActive}
          closeDrawer={closeDrawer}
          isDrawerOpen={isDrawerOpen}
          id={urlPath}
          label={subListItem.label}
          customLabel={subListItem.customLabel}
          isHidden={subListItem.hidden}
          badgeCount={isChatRoute ? chatUnread : undefined}
        />
      </Link>
    );
  }
);

export { DrawerNavListItem, DrawerListItem };
